// Editing where you normally travel to and from.
//
// Two ways in, because they suit different moments. Searching a postcode is
// what you do sitting down, setting up. "Use where I am now" is what you do
// standing in the place itself, which is both easier and more accurate.
import { useEffect, useRef, useState } from 'react';
import type { ServerPlace } from './useAuth';

export interface GeoResult {
  label: string;
  address?: string;
  lat: number;
  lon: number;
  source: 'postcode' | 'stoppoint';
}

export function PlaceEditor({
  place,
  currentCoords,
  onSearch,
  onSave,
  onClose,
}: {
  place: ServerPlace;
  currentCoords: { lat: number; lon: number } | null;
  onSearch: (q: string) => Promise<GeoResult[]>;
  onSave: (p: Partial<ServerPlace> & { placeKey: string }) => Promise<void>;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(place.label);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeoResult[]>([]);
  const [picked, setPicked] = useState<{ lat: number; lon: number; address?: string } | null>(
    place.lat !== undefined && place.lon !== undefined
      ? { lat: place.lat, lon: place.lon, address: place.address }
      : null
  );
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef<number | null>(null);

  // Debounced, so typing a postcode does not fire six lookups.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(() => {
      setSearching(true);
      void onSearch(query).then((r) => {
        setResults(r);
        setSearching(false);
      });
    }, 350);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, onSearch]);

  const save = async () => {
    if (!picked || saving) return;
    setSaving(true);
    await onSave({
      placeKey: place.placeKey,
      label: label.trim() || place.label,
      lat: picked.lat,
      lon: picked.lon,
      address: picked.address,
      isHome: place.isHome,
      isDefaultDestination: place.isDefaultDestination,
      pinned: true,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2>{place.isHome ? 'Where you travel from' : 'Where you travel to'}</h2>

        <label className="field">
          <span>Name it</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Home" />
        </label>

        <label className="field">
          <span>Postcode or station</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="TW9 1UW, or Richmond"
            autoCapitalize="characters"
            spellCheck={false}
          />
        </label>

        {searching && <p className="hint">Looking…</p>}

        {results.map((r, i) => (
          <button
            key={i}
            className="place-row"
            onClick={() => {
              setPicked({ lat: r.lat, lon: r.lon, address: r.address ?? r.label });
              setResults([]);
              setQuery('');
              if (!label.trim() || label === place.label) setLabel(place.label);
            }}
          >
            <span>{r.label}</span>
            <em>{r.address ?? (r.source === 'stoppoint' ? 'Station' : '')}</em>
          </button>
        ))}

        {currentCoords && (
          <button
            className="ghost"
            onClick={() =>
              setPicked({ lat: currentCoords.lat, lon: currentCoords.lon, address: undefined })
            }
          >
            Use where I am now
          </button>
        )}

        <div className="picked">
          {picked ? (
            <>
              <strong>{picked.address ?? 'Location set'}</strong>
              <em>
                {picked.lat.toFixed(4)}, {picked.lon.toFixed(4)}
              </em>
            </>
          ) : (
            <em>Nothing set yet. Search a postcode, or use where you are.</em>
          )}
        </div>

        <button className="primary" onClick={() => void save()} disabled={!picked || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
